import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  MultiSelect,
} from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { MessageTemplateType } from "@tellescope/types-models";
import { type TemplatesFilters } from "./templatesFilters";
import { useTemplatesFilterDialog } from "./useTemplatesFilterDialog";

export interface TemplatesFilterDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: TemplatesFilters;
  onApply: (filters: TemplatesFilters) => void;
}

export const TemplatesFilterDialog = ({ open, onOpenChange, filters, onApply }: TemplatesFilterDialogProps) => {
  const { t } = useTranslation("templates");
  const {
    staged,
    setField,
    typeOptions,
    channelOptions,
    roleOptions,
    entityOptions,
    userTagOptions,
    tagOptions,
    rolesLoading,
    entitiesLoading,
    handleApply,
    handleClearAll,
    canApply,
    canClear,
  } = useTemplatesFilterDialog({ open, filters, onApply, onOpenChange });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="templates-filter-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto" divided>
        <DialogHeader>
          <DialogTitle>{t("Filter Templates")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 px-6 pb-6">
          <div className="space-y-4">
            <div className="space-y-1.5">
              <span className="text-sm font-medium">{t("Type")}</span>
              <MultiSelect<MessageTemplateType>
                options={typeOptions}
                value={staged.type ?? []}
                onValueChange={(value) => setField("type", value.length ? value : undefined)}
                placeholder={t("Any type")}
              />
            </div>
            <div className="space-y-1.5">
              <span className="text-sm font-medium">{t("Channels")}</span>
              <MultiSelect
                options={channelOptions}
                value={staged.channels ?? []}
                onValueChange={(value) => setField("channels", value.length ? value : undefined)}
                placeholder={t("Any channel")}
              />
            </div>
            <div className="space-y-1.5">
              <span className="text-sm font-medium">{t("Roles")}</span>
              <MultiSelect
                options={roleOptions ?? []}
                value={staged.roles ?? []}
                onValueChange={(value) => setField("roles", value.length ? value : undefined)}
                placeholder={t("Any role")}
                isLoading={rolesLoading}
              />
            </div>
            <div className="space-y-1.5">
              <span className="text-sm font-medium">{t("Entities")}</span>
              <MultiSelect
                options={entityOptions}
                value={staged.entities ?? []}
                onValueChange={(value) => setField("entities", value.length ? value : undefined)}
                placeholder={t("Any entity")}
                isLoading={entitiesLoading}
              />
            </div>
            <div className="space-y-1.5">
              <span className="text-sm font-medium">{t("User Tags")}</span>
              <MultiSelect
                options={userTagOptions}
                value={staged.userTags ?? []}
                onValueChange={(value) => setField("userTags", value.length ? value : undefined)}
                placeholder={t("Any user tag")}
              />
            </div>
            <div className="space-y-1.5">
              <span className="text-sm font-medium">{t("Tags")}</span>
              <MultiSelect
                options={tagOptions}
                value={staged.tags ?? []}
                onValueChange={(value) => setField("tags", value.length ? value : undefined)}
                placeholder={t("Any tag")}
              />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox
              data-testid="templates-filter-show-archived"
              checked={!!staged.showArchived}
              onCheckedChange={(checked) => setField("showArchived", checked === true ? true : undefined)}
            />
            {t("Show archived")}
          </label>
        </div>
        <DialogFooter className="flex flex-row items-center justify-between sm:justify-between gap-2 bg-muted">
          <Button
            data-testid="templates-filter-clear-all"
            variant="ghost"
            onClick={handleClearAll}
            disabled={!canClear}
            className="font-light"
          >
            {t("Clear all")}
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="font-light">
              {t("Cancel")}
            </Button>
            <Button
              data-testid="templates-filter-apply"
              onClick={handleApply}
              disabled={!canApply}
              className="font-light"
            >
              {t("Apply")}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
